<!-- 章节信息选择-吴影茹 -->
<template>
  <div :style="{ width: width == '' ? '100%' : width }">
    <!-- 等待状态 主要用于数据回显等待 -->
    <div v-show="loading" style="position: relative">
      <div class="dummy_loading column_center_center" :style="{ height: loadingHeight + 'px' }">
        <loading-outlined />
      </div>
      <a-cascader v-model:value="dummyValue" :options="dummyData" :style="{ width: width }" id="DummyCascader" class="dummy_content" />
    </div>
    <!-- 正常选择 -->
    <div v-show="!loading">
      <a-cascader
        v-model:value="selectValue"
        :options="options"
        :load-data="loadData"
        :change-on-select="type == 'add' ? false : true"
        :placeholder="placeholder"
        @change="changeHandler"
        :style="{ width: width }"
        allowClear
      />
    </div>
  </div>
</template>
            
<script setup lang="ts">
import { onMounted, ref, onBeforeMount, nextTick, watch } from 'vue'
import { useDict } from '@/composable/useDict'
const { getDictData } = useDict()
import * as api from './service'
import { LoadingOutlined } from '@ant-design/icons-vue'

const props = defineProps({
  // 回显数据
  data: {
    type: Array as () => Array<any>,
    default: () => []
  },
  // 重置
  reset: {
    type: Boolean,
    default: false
  },
  // 类型:
  // search 搜索状态
  // add 新增状态(普通资源,即必须选到最后章节)
  // question_add 新增状态(试题/试卷,必须选择到教材,但可只选择到教材,不到最后一个节点)
  type: {
    type: String,
    default: 'search'
  },
  placeholder: {
    type: String,
    default: '请选择章节信息'
  },
  // 宽度
  width: {
    type: String,
    default: ''
  }
})

onMounted(() => {
  if (props.data && props.data.length != 0) loading.value = true
})
onBeforeMount(() => {
  getDictList()
})

const emit = defineEmits(['select', 'update:reset', 'update:data'])

// 如传入默认值 处理需要一定时间 使用loading展示等候 暂定方案 后续优化
const loading = ref(false)
const loadingHeight = ref(32)
// 用于展示loading组件的假数据
const dummyData = ref([])
const dummyValue = ref([])

// 选择状态 如是选择后传回的data值不进行加载回显（为了解决回显后继续选择仍然出现回显加载状态）
const selectType = ref(false)

const selectValue = ref<any>([])
const options = ref<any>([])

// 年级列表
const gradeList = ref<any>([])
// 学科列表
const subjectList = ref([])
// 册次列表
const volumeList = ref<any>([])
// 版本列表
const versionList = ref<any>([])
// 获取字典数据
const getDictList = async () => {
  // 使用学段作为首层选择数据
  options.value = await (
    await getDictData('study_period')
  ).map((item) => {
    return {
      ...item,
      isLeaf: false,
      level: 1
    }
  })
  gradeList.value = await getDictData('public_grade')
  subjectList.value = await getDictData('public_subject')
  volumeList.value = await (
    await getDictData('volume')
  ).map((item) => {
    return {
      ...item,
      isLeaf: false,
      level: 4
    }
  })
  api.versionList({}).then((res) => {
    if (res.data && res.data.length != 0) {
      versionList.value = res.data.map((item) => {
        return {
          ...item,
          value: item.versionId,
          label: item.versionTitle,
          isLeaf: false,
          level: 5
        }
      })
    }
  })
}

// 记录所选学段
const selectedPeriod = ref('')
// 记录所选年级code
const selectedGrade = ref('')
// 记录所选学科
const selectedSubject = ref('')
// 记录所选册次
const selectedVolume = ref('')
// 记录所选教材版本
const selectedVersion = ref('')
// 因教材都是默认第一本教材 不展示在下拉框中 所以选择时需记录所选的教材id和名称
const selectedTextbookId = ref('')
const selectedTextbookTitle = ref('')
// 加载数据
const loadData = async (selectedOptions) => {
  const targetOption = selectedOptions[selectedOptions.length - 1]
  targetOption.loading = true

  if (targetOption.level == 1) {
    selectedPeriod.value = targetOption.value
    // 选择学段 根据学段返回年级
    targetOption.children = gradeList.value
      .filter((item) => {
        return (
          item.dictGroup &&
          item.dictGroup.length != 0 &&
          item.dictGroup.findIndex((i) => {
            return i.value == targetOption.value
          }) != -1
        )
      })
      .map((item) => {
        return {
          ...item,
          isLeaf: false,
          level: 2
        }
      })
  } else if (targetOption.level == 2) {
    selectedGrade.value = targetOption.value
    // 选择年级 根据年级返回学校已添加的学科数据
    targetOption.children = subjectList.value
      .filter((item) => {
        return (
          item.dictGroup &&
          item.dictGroup.length != 0 &&
          item.dictGroup.findIndex((i) => {
            return i.value == targetOption.value
          }) != -1
        )
      })
      .map((item) => {
        return {
          ...item,
          isLeaf: false,
          level: 3
        }
      })
  } else if (targetOption.level == 3) {
    selectedSubject.value = targetOption.value
    // 选择学科 返回册次字典
    targetOption.children = volumeList.value.map((item) => {
      return { ...item }
    })
  } else if (targetOption.level == 4) {
    selectedVolume.value = targetOption.value
    // 选择册次 返回教材版本数据
    targetOption.children = versionList.value.map((item) => {
      return { ...item }
    })
  } else if (targetOption.level == 5) {
    selectedVersion.value = targetOption.value
    // 选择教材版本 返回前面所选的信息下的第一本教材的单元及章节数据
    api
      .textbookList({
        studyPeriod: selectedPeriod.value,
        gradeCode: selectedGrade.value,
        subjectCode: selectedSubject.value,
        volumeCode: selectedVolume.value,
        versionId: targetOption.value
      })
      .then((res) => {
        if (res.data && res.data.length != 0) {
          selectedTextbookId.value = res.data[0].textbookId
          selectedTextbookTitle.value = res.data[0].title
          // 如有教材 获取第一本教材的单元+章节数据
          api
            .unitList({
              textbookId: res.data[0].textbookId
            })
            .then((res) => {
              if (res.data && res.data.length != 0) {
                targetOption.children = res.data.map((item) => {
                  return {
                    ...item,
                    value: item.chapterId,
                    label: item.title,
                    isLeaf: item.childNodes && item.childNodes.length != 0 ? false : true,
                    level: 6,
                    children:
                      item.childNodes && item.childNodes.length != 0
                        ? item.childNodes.map((i) => {
                            return {
                              ...i,
                              value: i.chapterId,
                              label: i.title,
                              isLeaf: true,
                              level: 7
                            }
                          })
                        : null
                  }
                })
              } else {
                targetOption.children = []
              }
            })
        } else {
          targetOption.children = []
        }
      })
  }

  options.value = [...options.value]
}

// 延迟操作
const delay = (ms) => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

// 选择
const changeHandler = async (e, item) => {
  if (e) {
    // 先整理返回数据
    // 返回value值
    var returnValue = e.map((i) => {
      return i
    })
    // 如已选择到教材版本以后 需要插入章节信息
    if (e.length > 5) returnValue.splice(5, 0, selectedTextbookId.value)
    // 如是刚选到教材 需要等候一定时间 待教材列表接口返回数据后 再插入章节信息
    if (e.length == 5) {
      await delay(300)
      returnValue.splice(5, 0, selectedTextbookId.value)
    }

    // 返回完整数据值
    var returnItem = item.map((i) => {
      return {
        value: i.value,
        label: i.label
      }
    })
    // 如已选择到教材版本以后 插入章节信息
    if (e.length > 5) {
      returnItem.splice(5, 0, {
        value: selectedTextbookId.value,
        label: selectedTextbookTitle.value
      })
    }
    // 如是刚选到教材 需要等候一定时间 待教材列表接口返回数据后 再插入章节信息
    if (e.length == 5) {
      await delay(300)
      returnItem.splice(5, 0, {
        value: selectedTextbookId.value,
        label: selectedTextbookTitle.value
      })
    }

    // 如是新增试题类型 需要使用change-on-select 但必须选到教材后才返回值
    if (props.type == 'question_add' && returnValue.length < 5) {
      selectValue.value = []
    } else {
      selectType.value = true
      emit('select', returnValue, returnItem)
      emit('update:data', returnValue)
    }
  } else {
    emit('select', [], [])
    emit('update:data', [])
  }
}

// 获取所需内容的index 考虑到有单学段和多学段的情况 options的层级不同 需要反复编写同样的操作 因此封装一个方法
const getIndex = (level = 1, value, index1 = -1, index2 = -1, index3 = -1, index4 = -1) => {
  var index = -1
  if (level == 1) {
    index = options.value.findIndex((item) => {
      return item.value == value
    })
  } else if (level == 2) {
    if (options.value[index1].children)
      index = options.value[index1].children.findIndex((item) => {
        return item.value == value
      })
  } else if (level == 3) {
    if (options.value[index1].children && options.value[index1].children[index2].children)
      index = options.value[index1].children[index2].children.findIndex((item) => {
        return item.value == value
      })
  } else if (level == 4) {
    if (
      options.value[index1].children &&
      options.value[index1].children[index2].children &&
      options.value[index1].children[index2].children[index3].children
    )
      index = options.value[index1].children[index2].children[index3].children.findIndex((item) => {
        return item.value == value
      })
  } else if (level == 5) {
    if (
      options.value[index1].children &&
      options.value[index1].children[index2].children &&
      options.value[index1].children[index2].children[index3].children &&
      options.value[index1].children[index2].children[index3].children[index4].children
    )
      index = options.value[index1].children[index2].children[index3].children[index4].children.findIndex((item) => {
        return item.value == value
      })
  }

  return index
}

// 通过层级赋值 考虑到有单学段和多学段的情况 options的层级不同 需要反复编写同样的操作 因此封装一个方法
const setList = (level = 1, list, index1 = -1, index2 = -1, index3 = -1, index4 = -1, index5 = -1) => {
  if (level == 1) {
    options.value = list
  } else if (level == 2) {
    if (options.value[index1]) options.value[index1].children = list
  } else if (level == 3) {
    if (options.value[index1] && options.value[index1].children && options.value[index1].children[index2])
      options.value[index1].children[index2].children = list
  } else if (level == 4) {
    if (
      options.value[index1] &&
      options.value[index1].children &&
      options.value[index1].children[index2] &&
      options.value[index1].children[index2].children &&
      options.value[index1].children[index2].children[index3]
    )
      options.value[index1].children[index2].children[index3].children = list
  } else if (level == 5) {
    if (
      options.value[index1] &&
      options.value[index1].children &&
      options.value[index1].children[index2] &&
      options.value[index1].children[index2].children &&
      options.value[index1].children[index2].children[index3] &&
      options.value[index1].children[index2].children[index3].children &&
      options.value[index1].children[index2].children[index3].children[index4]
    )
      options.value[index1].children[index2].children[index3].children[index4].children = list
  } else if (level == 6) {
    if (
      options.value[index1] &&
      options.value[index1].children &&
      options.value[index1].children[index2] &&
      options.value[index1].children[index2].children &&
      options.value[index1].children[index2].children[index3] &&
      options.value[index1].children[index2].children[index3].children &&
      options.value[index1].children[index2].children[index3].children[index4] &&
      options.value[index1].children[index2].children[index3].children[index4].children &&
      options.value[index1].children[index2].children[index3].children[index4].children[index5]
    )
      options.value[index1].children[index2].children[index3].children[index4].children[index5].children = list
  }
}

// 监听默认值变化 回显 主要用于编辑
watch(
  () => props.data,
  async (newVal: any[]) => {
    if (!selectType.value) {
      // 如非选择后传入的值 进行回显
      if (newVal && newVal.length != 0) {
        loading.value = true
        // 预加载所选下数据
        setTimeout(async () => {
          selectValue.value = []
          // 通过学段获取学段下年级列表
          var periodIndex = -1
          // 获取默认值中的学段在options中的index
          periodIndex = await getIndex(1, newVal[0])
          if (periodIndex != -1) {
            // 在gradeList中筛选符合学段的年级数据
            var tempGradeList: any = []
            for (var i = 0; i < gradeList.value.length; i++) {
              if (
                gradeList.value[i].dictGroup &&
                gradeList.value[i].dictGroup.length != 0 &&
                gradeList.value[i].dictGroup.findIndex((grade) => {
                  return grade.value == newVal[0]
                }) != -1
              ) {
                tempGradeList.push({ ...gradeList.value[i], level: 2, isLeaf: false })
              }
            }

            await setList(2, tempGradeList, periodIndex)
          } else {
            loading.value = false
            return
          }

          // 如传入数据有年级 通过年级获取年级下学科列表
          var gradeIndex = -1
          if (newVal.length > 1) {
            selectedGrade.value = newVal[1]
            // 获取默认值中的年级在options中的index
            gradeIndex = await getIndex(2, newVal[1], periodIndex)
            if (gradeIndex != -1) {
              var tempSubjectList = subjectList.value
                .filter((item) => {
                  return (
                    item.dictGroup &&
                    item.dictGroup.length != 0 &&
                    item.dictGroup.findIndex((i) => {
                      return i.value == newVal[1]
                    }) != -1
                  )
                })
                .map((item) => {
                  return {
                    ...item,
                    isLeaf: false,
                    level: 3
                  }
                })
              await setList(3, tempSubjectList, periodIndex, gradeIndex)
            } else {
              loading.value = false
              return
            }
          }

          // 如传入数据有学科 获取册次数据
          var subjectIndex = -1
          if (newVal.length > 2) {
            selectedSubject.value = newVal[2]
            // 获取默认值中的学科在options中的index
            subjectIndex = await getIndex(3, newVal[2], periodIndex, gradeIndex)
            if (subjectIndex != -1) {
              await setList(4, volumeList.value, periodIndex, gradeIndex, subjectIndex)
            } else {
              loading.value = false
              return
            }
          }

          // 如传入数据有册次 获取教材版本数据
          var volumnIndex = -1
          if (newVal.length > 3) {
            selectedVolume.value = newVal[3]
            // 获取默认值中的册次在options中的index
            volumnIndex = await getIndex(4, newVal[3], periodIndex, gradeIndex, subjectIndex)
            if (volumnIndex != -1) {
              await setList(5, versionList.value, periodIndex, gradeIndex, subjectIndex, volumnIndex)
            } else {
              loading.value = false
              return
            }
          }

          // 如传入数据有教材版本 保存教材版本值
          if (newVal.length > 4) selectedVersion.value = newVal[4]
          // 如传入数据有教材 获取所选教材下的单元+章节数据
          if (newVal.length > 5) {
            selectedTextbookId.value = newVal[5]
            // 通过教材id获取教材列表 以存入教材名称
            api.textbookList({ ids: [newVal[5]] }).then((res) => {
              if (res.data && res.data.length != 0 && res.data[0].textbookId == newVal[5]) selectedTextbookTitle.value = res.data[0].title
            })

            // 获取默认值中的教材版本在options中的index
            var versionIndex = -1
            versionIndex = await getIndex(5, newVal[4], periodIndex, gradeIndex, subjectIndex, volumnIndex)
            if (versionIndex != -1) {
              var tempUnitList = []
              await api
                .unitList({
                  textbookId: newVal[5]
                })
                .then((res) => {
                  if (res.data && res.data.length != 0) {
                    tempUnitList = res.data.map((item) => {
                      return {
                        ...item,
                        value: item.chapterId,
                        label: item.title,
                        isLeaf: item.childNodes && item.childNodes.length != 0 ? false : true,
                        level: 6,
                        children:
                          item.childNodes && item.childNodes.length != 0
                            ? item.childNodes.map((i) => {
                                return {
                                  ...i,
                                  value: i.chapterId,
                                  label: i.title,
                                  isLeaf: true,
                                  level: 7
                                }
                              })
                            : null
                      }
                    })
                  } else {
                    loading.value = false
                    return
                  }
                })
              await setList(6, tempUnitList, periodIndex, gradeIndex, subjectIndex, volumnIndex, versionIndex)
            } else {
              loading.value = false
              return
            }
          }

          if (newVal[0]) selectValue.value.push(newVal[0])
          if (newVal[1]) selectValue.value.push(newVal[1])
          if (newVal[2]) selectValue.value.push(newVal[2])
          if (newVal[3]) selectValue.value.push(newVal[3])
          if (newVal[4]) selectValue.value.push(newVal[4])
          if (newVal[6]) selectValue.value.push(newVal[6])
          if (newVal[7]) selectValue.value.push(newVal[7])

          loading.value = false
        }, 3800)
      }
    } else {
      // 如是选择后传入的值 不进行回显 并重置选择状态值
      selectType.value = false
    }
  },
  { immediate: true, deep: true }
)

// 重置
watch(
  () => props.reset,
  (newVal) => {
    if (newVal) {
      selectValue.value = []
      selectedPeriod.value = ''
      selectedGrade.value = ''
      selectedSubject.value = ''
      selectedVolume.value = ''
      selectedVersion.value = ''
      selectedTextbookId.value = ''
      selectedTextbookTitle.value = ''
      emit('update:reset', false)
    }
  },
  { immediate: true }
)
</script>
            
<style lang="scss" scoped>
.dummy_content {
  ::v-deep(.ant-select-selector > span) {
    display: none;
  }
}
.dummy_loading {
  position: absolute;
  z-index: 10;
  left: 20px;
  top: 0;
}
</style>